import type { MelodyTake } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import type { FC, FocusEvent, KeyboardEvent } from 'react'
import { useRef, useState } from 'react'
import styles from './takes-list.module.css'

/**
 * The melody's takes, under the block header (melody toolkit plan M5 step 5.3; spec §5.5). `Hear` auditions
 * a take; `Use this take` makes it current; `Save as take` keeps the current notes as a new one.
 * The rules are `MelodyLib`'s takes functions; this lists them and turns clicks into calls.
 */

export interface TakesListProps {
  takes: MelodyTake[]
  currentTakeId?: string
  onAudition: (id: string) => void
  onUse: (id: string) => void
  onRename: (id: string, name: string) => void
  onSaveAsTake: () => void
  className?: string
}

export const TakesList: FC<TakesListProps> = props => {
  const { takes, currentTakeId, onAudition, onUse, onRename, onSaveAsTake, className } = props
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  /**
   * Set once Enter, Esc or a blur has ended the rename: the field then goes, and moving focus off it blurs
   * it, a blur that must neither rename a second time nor keep the name Esc dropped
   */
  const isClosing = useRef(false)
  /** Each take's Rename button, where focus goes back to once the rename ends (Joni, M1–M5 review) */
  const renameButtons = useRef(new Map<string, HTMLButtonElement>())

  const startRename = (take: MelodyTake) => {
    isClosing.current = false
    setRenaming({ id: take.id, name: take.name })
  }

  /** Ends the rename, keeping `name` unless it is null, and puts focus back on Rename when asked */
  const finish = (name: string | null, isRefocusing: boolean) => {
    if (!renaming || isClosing.current) {
      return
    }
    isClosing.current = true
    if (name !== null) {
      onRename(renaming.id, name)
    }
    setRenaming(null)
    if (isRefocusing) {
      renameButtons.current.get(renaming.id)?.focus()
    }
  }

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      finish(renaming?.name ?? null, true)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      finish(null, true)
    }
  }

  /** Focus that left for another control stays there; focus that went nowhere comes back to Rename */
  const handleNameBlur = (event: FocusEvent<HTMLInputElement>) =>
    finish(renaming?.name ?? null, event.relatedTarget === null)

  return (
    <div className={className ? `${styles.takes} ${className}` : styles.takes}>
      {takes.length > 0 ? (
        <ul aria-label="Takes" className={styles.list}>
          {takes.map(take => {
            const isCurrent = take.id === currentTakeId
            return (
              <li
                key={take.id}
                className={isCurrent ? `${styles.take} ${styles.current}` : styles.take}
                aria-current={isCurrent ? 'true' : undefined}
              >
                {renaming?.id === take.id ? (
                  <input
                    className={styles.nameField}
                    aria-label="Take name"
                    value={renaming.name}
                    autoFocus
                    onChange={event => setRenaming({ id: take.id, name: event.target.value })}
                    onKeyDown={handleNameKeyDown}
                    onBlur={handleNameBlur}
                  />
                ) : (
                  /* The name as the singer typed it: text, not a button's capitals (Joni, M1–M5 review) */
                  <span className={styles.name}>{take.name}</span>
                )}
                <Button size="small" aria-label={`Hear ${take.name}`} onClick={() => onAudition(take.id)}>
                  Hear
                </Button>
                {isCurrent ? null : (
                  <Button size="small" onClick={() => onUse(take.id)}>
                    Use this take
                  </Button>
                )}
                <Button
                  size="small"
                  ref={(button: HTMLButtonElement | null) => {
                    if (button) {
                      renameButtons.current.set(take.id, button)
                      return
                    }
                    renameButtons.current.delete(take.id)
                  }}
                  onClick={() => startRename(take)}
                >
                  Rename
                </Button>
              </li>
            )
          })}
        </ul>
      ) : null}
      <Button size="small" onClick={onSaveAsTake}>
        Save as take
      </Button>
    </div>
  )
}
